import { Box, CircularProgress, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHealthCheck } from "../../api/useHealthApi";

const Loading: React.FC = () => {
  return (
    <div className="flex h-screen justify-center items-center">
      <CircularProgress size={80} />
    </div>
  );
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        background: `conic-gradient(from -50deg at 50% 50%, ${theme.palette.secondary.main} 0deg, ${theme.palette.secondary.dark} 180deg, ${theme.palette.secondary.main} 360deg)`,
        overflowY: "scroll",
      }}
    >
      <div className="h-screen w-full flex justify-center sm:items-center">{children}</div>
    </Box>
  );
};

const Health: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useHealthCheck();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-white">{t("health.noData")}</div>
      </div>
    );
  }

  const getStatusColor = (status: string | null | undefined) => {
    if (status === "healthy") return "text-green-600 bg-green-50 border-green-500";
    return "text-red-600 bg-red-50 border-red-500";
  };

  const getStatusBadgeColor = (status: string | null | undefined) => {
    if (status === "healthy") return "bg-green-500 text-white";
    return "bg-red-500 text-white";
  };

  return (
    <div className="w-full max-w-4xl rounded-lg border bg-white p-8 shadow-lg">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">{t("health.title")}</h1>
        <span className={`rounded-full px-4 py-2 text-sm font-semibold ${getStatusBadgeColor(data.status)}`}>
          {t(`health.status.${data.status}`)}
        </span>
      </div>

      <div className="mb-6 text-sm text-gray-600">
        {t("health.lastChecked")} {data.timestamp ? new Date(data.timestamp).toLocaleString() : ""}
      </div>

      <div className="space-y-4">
        {data.checks &&
          Object.entries(data.checks).map(([name, check]) => (
            <div key={name} className={`rounded-lg border p-4 ${getStatusColor(check.status)}`}>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-xl font-semibold">{t(`health.checks.${name}`, name)}</h2>
                <span className={`rounded px-3 py-1 text-sm font-semibold ${getStatusBadgeColor(check.status)}`}>
                  {t(`health.status.${check.status}`)}
                </span>
              </div>
              <div className="flex items-center justify-end text-sm">
                {check.error && (
                  <div className="text-right">
                    <span className="font-semibold">{t("health.error")} </span>
                    <span>{check.error}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

const HealthPage: React.FC = () => {
  return (
    <Layout>
      <Health />
    </Layout>
  );
};

export default HealthPage;
